import React, { Suspense, lazy, useEffect, useState } from "react";
import "./App.css";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { SnackbarProvider } from "notistack";
import Slide from "@mui/material/Slide";
import { CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import Header from "./components/header/Header";
import Sidebar from "./components/sidebar/Sidebar";
import { PageLoadingState } from "./components/ui/StatePanel";
import { BackOfficeProvider } from "./backoffice/BackOfficeProvider";
import { getToken, refreshAccessToken } from "./helper/AxiosInstance";
import { LayoutProvider } from "./context/LayoutContext";
import MuiTheme from "./theme/MuiTheme";
import { getOnboardingAccess } from "./features/sellerOnboarding/api";

const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const AnalyticsDashboardPage = lazy(() => import("./features/analytics/pages/AnalyticsDashboardPage"));
const ListedProductsPage = lazy(() => import("./pages/ListedProductsPage"));
const QuantityManagementPage = lazy(() => import("./pages/QuantityManagementPage"));
const OrdersPage = lazy(() => import("./pages/OrdersPage"));
const WalletPage = lazy(() => import("./pages/WalletPage"));
const RemmitancePage = lazy(() => import("./pages/RemmitancePage"));
const AddProductPage = lazy(() => import("./pages/AddProductPage"));
const EditUpload = lazy(() => import("./pages/EditUpload"));
const BannerDashboard = lazy(() => import("./pages/BannerDashboard"));
const LeadsPage = lazy(() => import("./pages/LeadsPage"));
const LeadDetailPage = lazy(() => import("./pages/LeadDetailPage"));
const AdsTrackingPage = lazy(() => import("./pages/AdsTrackingPage"));
const PricingAnalyticsPage = lazy(() => import("./pages/admin/pricing/PricingAnalyticsPage"));
const PricingCalculatorPage = lazy(() => import("./pages/admin/pricing/PricingCalculatorPage"));
const PricingConfigFormPage = lazy(() => import("./pages/admin/pricing/PricingConfigFormPage"));
const PricingConfigListPage = lazy(() => import("./pages/admin/pricing/PricingConfigListPage"));
const CatalogPricingListPage = lazy(() => import("./pages/admin/pricing/CatalogPricingListPage"));
const CatalogPricingEditPage = lazy(() => import("./pages/admin/pricing/CatalogPricingEditPage"));
const AdminVendorsPage = lazy(() => import("./features/sellerOnboarding/AdminVendorsPage"));
const AdminReviewPage = lazy(() => import("./features/sellerOnboarding/AdminReviewPage"));
const AdminVendorDetailPage = lazy(() => import("./features/sellerOnboarding/AdminVendorDetailPage"));
const SellerLayout = lazy(() => import("./features/sellerOnboarding/SellerLayout"));
const SellerOverview = lazy(() => import("./features/sellerOnboarding/SellerPages").then((module) => ({ default: module.SellerOverview })));
const SellerBusiness = lazy(() => import("./features/sellerOnboarding/SellerPages").then((module) => ({ default: module.SellerBusiness })));
const SellerAddresses = lazy(() => import("./features/sellerOnboarding/SellerPages").then((module) => ({ default: module.SellerAddresses })));
const SellerDocuments = lazy(() => import("./features/sellerOnboarding/SellerPages").then((module) => ({ default: module.SellerDocuments })));
const SellerStatus = lazy(() => import("./features/sellerOnboarding/SellerPages").then((module) => ({ default: module.SellerStatus })));
const SellerActivationPage = lazy(() => import("./features/sellerOnboarding/SellerPages").then((module) => ({ default: module.SellerActivationPage })));
const Login = lazy(() => import("./components/login/Login"));
const Signup = lazy(() => import("./components/signup/Signup"));

function hasUsableAccessToken() {
  const token = getToken();
  if (!token) return false;
  try {
    const segment = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(window.atob(segment.padEnd(Math.ceil(segment.length / 4) * 4, "=")));
    return !payload.exp || payload.exp > Math.floor(Date.now() / 1000) + 15;
  } catch (_) {
    return false;
  }
}

function RequireAuth() {
  const location = useLocation();
  const [state, setState] = useState(() => hasUsableAccessToken() ? "ready" : "checking");

  useEffect(() => {
    if (hasUsableAccessToken()) {
      setState("ready");
      return undefined;
    }
    let active = true;
    refreshAccessToken().then(() => active && setState("ready")).catch(() => {
      localStorage.removeItem("token"); localStorage.removeItem("res"); localStorage.removeItem("email");
      if (active) setState("guest");
    });
    return () => { active = false; };
  }, [location.pathname]);

  if (state === "checking") return <PageLoadingState />;
  if (state === "guest") return <Navigate to="/login" replace state={{ from: location }} />;
  return <Outlet />;
}

function AdminLayout() {
  const [role, setRole] = useState("checking");
  const location = useLocation();
  useEffect(() => {
    let active = true;
    getOnboardingAccess().then(({ data }) => active && setRole(data.role))
      .catch(() => active && setRole("none"));
    return () => { active = false; };
  }, [location.pathname]);
  if (role === "checking") return <PageLoadingState />;
  if (role !== "admin") return <Navigate to={role === "seller" ? "/seller" : "/login"} replace />;
  return <div className="adminShell"><Header /><div className="adminLayout"><Sidebar /><main className="adminMain"><Suspense fallback={<PageLoadingState />}><Outlet /></Suspense></main></div></div>;
}

function SellerRoleGate({ children }) {
  const [role, setRole] = useState("checking");
  const location = useLocation();
  useEffect(() => {
    let active = true;
    getOnboardingAccess().then(({ data }) => active && setRole(data.role))
      .catch(() => active && setRole("none"));
    return () => { active = false; };
  }, [location.pathname]);
  if (role === "checking") return <PageLoadingState />;
  if (role !== "seller") return <Navigate to={role === "admin" ? "/vendors" : "/login"} replace />;
  return children;
}

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(() => window.localStorage.getItem("admin-sidebar") !== "collapsed");
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const setPersistentSidebarOpen = (value) => setSidebarOpen((current) => {
    const next = typeof value === "function" ? value(current) : value;
    window.localStorage.setItem("admin-sidebar", next ? "expanded" : "collapsed");
    return next;
  });

  return (
    <SnackbarProvider maxSnack={3} autoHideDuration={4000} anchorOrigin={{ vertical: "top", horizontal: "right" }} TransitionComponent={Slide}>
      <ThemeProvider theme={MuiTheme}><CssBaseline />
        <LayoutProvider value={{ sidebarOpen, setSidebarOpen: setPersistentSidebarOpen, mobileDrawerOpen, setMobileDrawerOpen }}>
          <BackOfficeProvider><Suspense fallback={<PageLoadingState />}><Routes>
            <Route path="/login" element={hasUsableAccessToken() ? <Navigate to="/dashboard" replace /> : <Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/seller/activate" element={<SellerActivationPage />} />
            <Route element={<RequireAuth />}>
              <Route path="/seller" element={<SellerRoleGate><SellerLayout /></SellerRoleGate>}>
                <Route index element={<SellerOverview />} />
                <Route path="business" element={<SellerBusiness />} />
                <Route path="addresses" element={<SellerAddresses />} />
                <Route path="documents" element={<SellerDocuments />} />
                <Route path="status" element={<SellerStatus />} />
              </Route>
              <Route element={<AdminLayout />}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/vendors" element={<AdminVendorsPage />} />
                <Route path="/vendors/:vendorId" element={<AdminVendorDetailPage />} />
                <Route path="/onboarding" element={<AdminReviewPage />} />
                <Route path="/analytics" element={<AnalyticsDashboardPage />} />
                <Route path="/listed-products" element={<ListedProductsPage />} />
                <Route path="/QManagement" element={<QuantityManagementPage />} />
                <Route path="/orders" element={<OrdersPage />} />
                <Route path="/orders/:orderId" element={<OrdersPage />} />
                <Route path="/wallet" element={<WalletPage />} />
                <Route path="/remmitance" element={<RemmitancePage />} />
                <Route path="/addproduct" element={<AddProductPage />} />
                <Route path="/edit-upload" element={<EditUpload />} />
                <Route path="/banner" element={<BannerDashboard />} />
                <Route path="/leads" element={<LeadsPage />} />
                <Route path="/leads/:leadId" element={<LeadDetailPage />} />
                <Route path="/leads/ads-tracking" element={<AdsTrackingPage />} />
                <Route path="/pricing" element={<PricingAnalyticsPage />} />
                <Route path="/pricing/configs" element={<PricingConfigListPage />} />
                <Route path="/pricing/configs/:id" element={<PricingConfigFormPage />} />
                <Route path="/pricing/calculator/:variantId" element={<PricingCalculatorPage />} />
                <Route path="/pricing/catalog" element={<CatalogPricingListPage />} />
                <Route path="/pricing/catalog/:qtyPriceId" element={<CatalogPricingEditPage />} />
              </Route>
            </Route>
            <Route path="/" element={<Navigate to={getToken() ? "/dashboard" : "/login"} replace />} />
            <Route path="*" element={<Navigate to={getToken() ? "/dashboard" : "/login"} replace />} />
          </Routes></Suspense></BackOfficeProvider>
        </LayoutProvider>
      </ThemeProvider>
    </SnackbarProvider>
  );
}

export default App;
